import axios from 'axios';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Important for cookie handling
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000
});

// Request interceptor to add auth token if available
apiClient.interceptors.request.use(
  (config) => {
    // You can add authorization headers here if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      // Redirect to login if unauthorized
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// Authentication API
export const authService = {
  // Set session cookie from Firebase ID token
  setSession: async (idToken) => {
    try {
      const response = await apiClient.post('/auth/set-session', { idToken });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Verify Firebase token (backward compatibility)
  verifyToken: async (token) => {
    try {
      const response = await apiClient.post('/auth/verify-token', { token });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Logout and clear session
  logout: async (revokeAll = false) => {
    try {
      const response = await apiClient.post('/auth/logout', { revokeAll });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get current session info
  getSessionInfo: async () => {
    try {
      const response = await apiClient.get('/auth/session-info');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
};

// User API
export const userService = {
  // Get user profile by UID
  getUser: async (uid) => {
    try {
      const response = await apiClient.get(`/users/${uid}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get current user's profile
  getMyProfile: async () => {
    try {
      const response = await apiClient.get('/users/me/profile');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Create new user profile
  createUser: async (userData) => {
    try {
      const response = await apiClient.post('/users', userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Update user profile
  updateUser: async (uid, userData) => {
    try {
      const response = await apiClient.put(`/users/${uid}`, userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Delete user profile (soft delete)
  deleteUser: async (uid) => {
    try {
      const response = await apiClient.delete(`/users/${uid}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get user statistics
  getUserStats: async () => {
    try {
      const response = await apiClient.get('/users/stats/summary');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
};

// Health check
export const healthService = {
  check: async () => {
    try {
      const response = await apiClient.get('/health');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
};

export default apiClient; 